/**
 * @docs docs/features/website-seo-performance
 */

import useContext from '@/client/context';
import useProductSession from '@/client/hooks/useProductSession';
import { identifyMixpanelUser, scheduleMixpanelInit } from '@/client/services/metrics/mixpanel';
import {
    identifyBrevoContact,
    initBrevoTracker,
    trackBrevoPageView,
} from '@uniquedomains/components/runtime/brevoTracker';

const GA_MEASUREMENT_ID = 'G-X6B08W1L0W';
const CRISP_WEBSITE_ID = '20909173-17f3-48bc-af45-5310e4e804b7';

const SCRIPT_IDS = {
    gaConfig: 'ud-website-ga-config',
    gaLoader: 'ud-website-ga-loader',
    crisp: 'ud-website-crisp-loader',
} as const;

const hasScript = (scripts: Array<{ id?: string }>, id: string) => {
    return scripts.some((script) => script.id === id);
};

/*
 * Third-party tags load after `load`, in an idle slot, from an inline script.
 * A `page.scripts` url entry is emitted with `<link rel="preload" as="script">`, which
 * fetches the tag at High priority next to the render-critical CSS: gtag.js (about 175 KB)
 * was competing with first paint on every page, and the Crisp client is about 300 KB. The
 * inline form has no preload, and the bounded idle wait keeps the tag off the hydration
 * tasks. `condition` is a browser expression; the tag is skipped when it is false.
 */
const buildIdleScriptLoader = ({
    src,
    timeoutMs,
    condition,
}: {
    src: string;
    timeoutMs: number;
    condition?: string;
}): string =>
    `
(function () {
    ${condition ? `if (!(${condition})) return;` : ''}
    var load = function () {
        var s = document.createElement("script");
        s.src = ${JSON.stringify(src)};
        s.async = true;
        document.head.appendChild(s);
    };
    var schedule = function () {
        if ("requestIdleCallback" in window) window.requestIdleCallback(load, { timeout: ${timeoutMs} });
        else window.setTimeout(load, ${timeoutMs});
    };
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });
})();
`.trim();

const MetricsComponent = () => {
    const { page, publicOrigins } = useContext();
    /*
     * The Website has no auth plugin, so `useContext().user` is null on every page: the
     * identity these two trackers carried was always anonymous. It comes from the Product
     * session probe instead, which this component shares with the header it renders
     * inside, so it costs no extra request. `name` is not on that contract; both calls
     * treat a missing name as absent.
     * See docs/fixes/2026-09-14-website-header-blind-to-members.md.
     */
    const { status, session } = useProductSession(publicOrigins.productOrigin);
    const identifiedEmail = status === 'member' ? session?.email ?? null : null;

    scheduleMixpanelInit();
    identifyMixpanelUser({ email: identifiedEmail, name: null });

    /*
     * Brevo is installed from the browser rather than pushed onto `page.scripts` like GA
     * and Crisp: `page.scripts` is only rendered by the SSR document, so a script added
     * there never lands on a client-side navigation. Each call below is idempotent, so
     * re-running them on every render of this component is what keeps the tracker, the
     * contact identity and the page path correct after the router moves.
     */
    initBrevoTracker({ isDevBuild: __DEV__ });
    identifyBrevoContact({ email: identifiedEmail });
    trackBrevoPageView();

    // The header's member probe hits the Product origin on every load; the connection is
    // opened alongside the stylesheets instead of after hydration (about 370 ms on a phone).
    if (!page.head.some((tag) => tag.rel === 'preconnect' && tag.href === publicOrigins.productOrigin)) {
        page.head.push({ $: 'link', rel: 'preconnect', href: publicOrigins.productOrigin, crossorigin: 'anonymous' });
    }

    if (!__DEV__) {
        if (!hasScript(page.scripts, SCRIPT_IDS.gaConfig)) {
            // The queue is set up inline and at once, so the page view keeps its real
            // timestamp; gtag.js replays `dataLayer` whenever it arrives.
            page.scripts.push({
                id: SCRIPT_IDS.gaConfig,
                inline: `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${GA_MEASUREMENT_ID}');
                `.trim(),
            });
        }

        if (!hasScript(page.scripts, SCRIPT_IDS.gaLoader)) {
            page.scripts.push({
                id: SCRIPT_IDS.gaLoader,
                inline: buildIdleScriptLoader({
                    src: `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`,
                    timeoutMs: 3000,
                }),
            });
        }

        if (!hasScript(page.scripts, SCRIPT_IDS.crisp)) {
            /*
             * Desktop only. On a phone the bubble would sit on the fixed action bar, so the
             * phone reaches Contact from the menu instead (2026-09 redesign). The breakpoint
             * matches the `lg` switch of the action bar.
             */
            page.scripts.push({
                id: SCRIPT_IDS.crisp,
                inline: `
window.$crisp = [];
window.CRISP_WEBSITE_ID = "${CRISP_WEBSITE_ID}";
${buildIdleScriptLoader({
    src: 'https://client.crisp.chat/l.js',
    timeoutMs: 4000,
    condition: 'window.matchMedia("(min-width: 1024px)").matches',
})}
            `.trim(),
            });
        }
    }

    return null;
};

export default MetricsComponent;
